import InputField from 'components/Generic/Forms/FormFields/InputField';
import BaseSearchSuggests from 'components/Generic/SearchBar/BaseSearchSuggests';
import AbrdnButton from 'components/Generic/Buttons/AbrdnButton';
import { useDispatch, useSelector } from 'react-redux';
import { getSearchByISIN, getSearchQuery } from '@/store/fundCentre';
import { getCookie, setCookie } from 'hooks/useCookie';

import { useI18n } from 'next-localization';
import styles from './BaseSearch.module.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import { fundCentreActions } from '@/store/fundCentre';
import useHelpers from 'hooks/helpers';
import AnimateHeight from 'react-animate-height';

type FundObject = {
  name: string;
  title?: string;
  id: string;
};

type SearchResultsProp = {
  results: Array<FundObject>;
  searchMessageText: string;
};

type Props = {
  useAutosuggestions?: boolean;
  allowSearchByISIN: boolean;
  cookieName: string;
  placeholder?: string;
  fieldName?: string;
  label?: string;
  getSuggestions: (value) => void;
  isFetching: boolean;
  searchResultsProp: SearchResultsProp;
};

const iconSlot = <i className={`${styles['search-icon']} bi bi-search`} />;
const SEARCH_BY_ISIN_PATTERN = /^[a-zA-Z]{2}\d/; // letter + letter + number e.g. AB1

const BaseSearch = ({
  useAutosuggestions = true,
  allowSearchByISIN,
  cookieName,
  fieldName,
  label,
  placeholder,
  getSuggestions,
  isFetching,
  searchResultsProp,
}: Props) => {
  const { t } = useI18n();
  const dispatch = useDispatch();
  const searchByISIN = useSelector(getSearchByISIN);
  const searchQuery = useSelector(getSearchQuery);
  const { updateUrlParams, isFocusWithin } = useHelpers();
  const searchBox = useRef<HTMLDivElement>();

  const [height, setHeight] = useState<number | string>(0);
  const [searchInputData, setSearchInputData] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [screenReaderContent, setScreenReaderContent] = useState('');
  const [searchMessageText, setSearchMessageText] = useState('');
  const [showAutosuggestions, setShowAutosuggestions] = useState(false);
  const [focusedOption, setFocusedOption] = useState(null);
  const [customError, setCustomError] = useState(false);

  const searchField = useMemo(() => {
    return {
      label: {
        value: '',
      },
      placeholder: {
        value: placeholder,
      },
      errorMessages: {
        customError: t('fundCentreIncorrectISINCode'),
      },
      name: {
        value: fieldName,
      },
      hint: {
        value: '',
      },
      required: {
        value: false,
      },
      type: {
        value: 'search',
      },
      value: {
        value: searchInputData,
      },
      searchByISIN: false,
    };
  }, [searchInputData, fieldName, placeholder, t]);

  const handleClickOutside = (evt: MouseEvent) => {
    if (!searchBox.current?.contains(evt.target)) hideAutoSuggestions();
  };

  const hideAutoSuggestions = () => {
    setScreenReaderContent('');
    setShowAutosuggestions(false);
    setHeight(0);
  };

  const checkFocusWithin = (event: FocusEvent) => {
    if (!isFocusWithin(event) && showAutosuggestions) {
      setShowAutosuggestions(false);
    }
  };

  const onSearchValueUpdate = (inputValue: string, needSuggestions = true) => {
    setSearchInputData(inputValue);
    setCustomError(false);
    updateUrlParams([
      {
        name: 'search',
        value: inputValue,
      },
    ]);
    if (inputValue.length >= 3 && needSuggestions && !searchByISIN) {
      getSuggestions(inputValue);
    } else {
      setSearchResults([]);
      setSearchMessageText('');
    }
  };

  const onSearchFocus = () => {
    if (searchInputData) return;
    const searchHistory = getSearchHistory();
    if (!searchHistory.length) return;
    setSearchResults(searchHistory);
    setSearchMessageText(t('recentSearchResults'));
  };

  const updateSearchQueryState = (suggestionName?: string, fundId?: string) => {
    if (isValid()) {
      if (searchQuery === suggestionName) return;
      dispatch(
        fundCentreActions.setSearchQuery({
          name: suggestionName,
          id: fundId || '',
        })
      );
      if (suggestionName) {
        updateSearchHistory({
          name: suggestionName,
          id: fundId || '',
        });
      }
    }
  };

  const updateSearchHistory = (fundObject: FundObject) => {
    if (!fundObject.name) return;
    const records = getSearchHistory();
    const alreadyExistingIdx = records.findIndex((record) => record.name === fundObject.name);
    if (alreadyExistingIdx !== -1) {
      records.splice(alreadyExistingIdx, 1);
    } else {
      records.splice(4, 1);
    }
    records.unshift(fundObject);
    setCookie(cookieName, JSON.stringify(records));
    return records;
  };

  const getSearchHistory = () => {
    return JSON.parse(getCookie(cookieName) || '[]');
  };

  const handleSearchByISIN = (value: string) => {
    dispatch(fundCentreActions.setSearchByISIN(true));
    onSearchValueUpdate(value, false);
  };

  const handleRegularSearch = (value: string, needSuggestions?: boolean) => {
    onSearchValueUpdate(value, needSuggestions);
  };

  const searchInputHandler = (value: string, needSuggestions?: boolean) => {
    if (value === searchInputData) return;
    dispatch(fundCentreActions.setSearchByISIN(false));
    const isMatchSearchPattern = SEARCH_BY_ISIN_PATTERN.test(value);

    if (allowSearchByISIN && isMatchSearchPattern) {
      handleSearchByISIN(value);
    } else {
      handleRegularSearch(value, needSuggestions);
    }
  };

  const onFocusHandler = () => {
    setHeight('auto');
    setShowAutosuggestions(true);
    setScreenReaderContent('');
    onSearchFocus();
  };

  const selectFirstFilterOption = () => {
    if (!formattedSearchResults.length) return;
    const firstOption = document.getElementById('suggestion-0');

    firstOption.focus();
  };

  const selectFilterOptionWithArrow = (payload: { direction: string; idx: number }) => {
    const { direction, idx } = payload;

    if (direction === 'down') {
      const nextElement = document.getElementById('suggestion-' + (idx + 1));
      if (idx === formattedSearchResults.length - 1) {
        selectFirstFilterOption();
      }
      if (nextElement) {
        nextElement.focus();
      }
      setFocusedOption('suggestion-' + idx);
    } else if (direction === 'up') {
      const previousElement = document.getElementById('suggestion-' + (idx - 1));
      if (idx === 0) {
        selectLastFilterOption();
      }
      if (previousElement) {
        previousElement.focus();
      }
    }
  };

  const suggestOptionFocusHandler = (idx: number) => {
    setScreenReaderContent(`${idx + 1} of ${formattedSearchResults.length} is selected`);
  };

  const selectLastFilterOption = () => {
    if (!formattedSearchResults.length) return;
    const lastOption = document.getElementById('suggestion-' + (formattedSearchResults.length - 1));

    lastOption.focus();
  };

  const onSuggestionSelect = (suggestion: FundObject) => {
    const isMatchSearchPattern = SEARCH_BY_ISIN_PATTERN.test(suggestion);
    if (isMatchSearchPattern) {
      dispatch(fundCentreActions.setSearchByISIN(true));
    }
    searchInputHandler(suggestion.name, false);
    updateSearchQueryState(suggestion.name, suggestion.id);
    setShowAutosuggestions(false);
  };

  const onSearchAction = () => {
    updateSearchQueryState(searchInputData);
    document.activeElement.blur();
    hideAutoSuggestions();
  };

  const isValid = () => {
    if (searchByISIN && (searchInputData.length < 12 || searchInputData.length > 12)) {
      setCustomError(true);
      return false;
    } else {
      return true;
    }
  };

  const formattedSearchResults = useMemo(() => {
    return !!searchResults
      ? searchResults.map((result: FundObject) => {
          // Create a new object with an added 'name' property
          return result.title ? { ...result, name: result.title } : result;
        })
      : [];
  }, [searchResults]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);

    const urlParams = updateUrlParams();
    if (urlParams.search) {
      onSearchValueUpdate(urlParams.search, false);
      if (SEARCH_BY_ISIN_PATTERN.test(urlParams.search)) {
        dispatch(fundCentreActions.setSearchByISIN(true));
        isValid();
      }
    }

    return () => document.removeEventListener('click', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchMessageText(searchResultsProp.searchMessageText);
    setSearchResults(searchResultsProp.results);
  }, [searchResultsProp]);

  const getDescription = allowSearchByISIN ? t('fundCentreSearchHint') : label ? t(label) : '';
  return (
    <form
      action="#"
      className="funds-search__form mt-2 d-flex align-items-center gap-3"
      onBlur={checkFocusWithin}
      onKeyDown={(e) => {
        if (e.code === 'Escape') {
          hideAutoSuggestions();
        }
      }}
      onSubmit={(e) => {
        e.preventDefault();
        onSearchAction();
      }}
    >
      <div className={styles['search-box']} ref={searchBox}>
        <div id="autocomplete-status" role="status" aria-live="polite" className="sr-only">
          {screenReaderContent}
        </div>
        <InputField
          fields={searchField}
          description={getDescription}
          descriptionClassName={styles.description}
          aria-haspopup="listbox"
          aria-owns="autocomplete-suggestions"
          aria-activedescendant={focusedOption}
          className={styles['search-field']}
          customError={customError}
          slots={{
            leftIcon: iconSlot,
          }}
          onChange={searchInputHandler}
          onFocus={onFocusHandler}
          selectFirstFilterOption={selectFirstFilterOption}
        />
        {useAutosuggestions && (
          <AnimateHeight height={height} duration={200} className={styles.autosuggestions}>
            <BaseSearchSuggests
              id="autocomplete-suggestions"
              messageText={searchMessageText}
              suggestions={formattedSearchResults}
              onSelect={onSuggestionSelect}
              onSelectFilterOptionWithArrow={selectFilterOptionWithArrow}
              onSuggestOptionFocusHandler={suggestOptionFocusHandler}
              isFetching={isFetching}
            />
          </AnimateHeight>
        )}
      </div>
      <AbrdnButton
        buttonType="primary"
        type="submit"
        className={`${styles['submit-button']} ${!allowSearchByISIN && !label ? 'mt-0' : ''}`}
      >
        <span>{t('search')}</span>
      </AbrdnButton>
    </form>
  );
};

export default BaseSearch;
